import {Component, effect, inject, OnDestroy, Signal} from '@angular/core';
import {Store} from '@ngrx/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {SettingsReferenceEnum} from '../../models';
import {selectSelectedSetting, selectSelectedStoreShelfTalkerOverrideSettings} from '../../store/setting.selectors';
import {
  ICrudOptions,
  IUserAccessFeatureShelfTalkerSettings,
} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccessFeatureShelfTalkerSettingsForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';
import {settingsSetSelected, settingStoreOverrides} from '../../store/setting.actions';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {IStoreOverrideSettings} from '../../models';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {IHasUnsavedChanges, UnsavedChangesService} from '../../services/unsaved-changes/unsaved-changes.service';

@Component({
  selector: 'app-feature-shelf-talkers-general',
  templateUrl: './feature-shelf-talkers-general.component.html',
  styleUrl: './feature-shelf-talkers-general.component.scss',
  standalone: false,
})
export class FeatureShelfTalkersGeneralComponent implements OnDestroy, IHasUnsavedChanges {

  //#region INJECTORS
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  private unsavedChangesService = inject(UnsavedChangesService);
  //#endregion

  //#region PROPERTIES
  autoGenerate: boolean = true;

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly _overrideSettings: Signal<IStoreOverrideSettings> = this._store.selectSignal(selectSelectedStoreShelfTalkerOverrideSettings);
  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _uaShelfTalkers: Signal<IUserAccessFeatureShelfTalkerSettings> = this._store.selectSignal(selectUserAccessFeatureShelfTalkerSettingsForSelectedStore);
  #hasOverridesBeenEdited: boolean = false;
  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const override = this._overrideSettings();
      const access = this._uaShelfTalkers();
      this.initializeForms(access, override);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
      // if (this.haveAnyEditsBeenMade && ref !== SettingsReferenceEnum.accountUserProfile) {
      //   void this.onShowModalUnsavedChanges(ref);
      // } else {
      //   this._store.dispatch(setSelectedSettings.setSelectedReferenceForNavigation({ref}));
      // }
    });

    // Register with unsaved changes service
    this.unsavedChangesService.registerComponent(this);
  }

  //#endregion

  //#region ACCESSOR - OVERRIDES

  get hasOverridesBeenEdited(): boolean {
    return this.#hasOverridesBeenEdited;
  }

  get isOverridesSubmitDisabled(): boolean {
    return (this.canOverridesBeEdited === false) || (this.canOverridesBeEdited === true && this.hasOverridesBeenEdited === false);
  }

  get canOverridesBeEdited(): boolean {
    const access = this.userSettingsUserAccess?.overrides;
    return access?.canEdit ?? false;
  }

  get canOverridesBeSubmitted(): boolean {
    const access = this.userSettingsUserAccess?.overrides;
    return access?.canSubmit ?? false;
  }

  get canOverridesBeViewed(): boolean {
    const access = this.userSettingsUserAccess?.overrides;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - OVERRIDE SETTINGS + USER ACCESS
  get overrideSettings(): IStoreOverrideSettings {
    return this._overrideSettings();
  }

  get selectedStore(): IStore {
    return this._selectedStore();
  }

  get userSettingsUserAccess(): IUserAccessFeatureShelfTalkerSettings {
    return this._uaShelfTalkers();
  }

  //#endregion

  //#region ACCESSOR - ALL/GLOBAL
  get haveAnyEditsBeenMade(): boolean {
    return (this.#hasOverridesBeenEdited);
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
      case 'overrides':
      default:
        this.revertOverrides();
        break;
    }
  }

  onSubmitForm(form: string): void {
    switch (form) {
      case 'all':
      case 'overrides':
        if (!this.canOverridesBeSubmitted) break;
        this.#hasOverridesBeenEdited = false;
        break;
      default:
        break;
    }
    this._store.dispatch(settingStoreOverrides.setStoreOverride({
      overrides: {
        ...this.overrideSettings,
        shelfTalkersEnabled: this.autoGenerate,
      },
    }));
  }

  onToggleSwitch(_: string, toggleValue: boolean): void {
    if (this.autoGenerate !== toggleValue) {
      this.autoGenerate = toggleValue;
      this.#hasOverridesBeenEdited = true;
    }
  }

  //#endregion

  //#region LIFECYCLE

  ngOnDestroy(): void {
    this.unsavedChangesService.unregisterComponent();
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  private initializeForms(access: IUserAccessFeatureShelfTalkerSettings, override: IStoreOverrideSettings): void {
    this.buildOverridesForm(access?.overrides, override?.shelfTalkersEnabled);
  }

  private buildOverridesForm(access: ICrudOptions, autoGenerate: boolean): void {
    if (access?.canView && this.hasOverridesBeenEdited === false) {
      if (access?.canView && this.hasOverridesBeenEdited === false) {
        this.autoGenerate = autoGenerate;
      }
    }
  }

  //#endregion

  //#region FORM - REVERT FORM EDITS
  revertOverrides(): void {
    this.autoGenerate = this.overrideSettings?.shelfTalkersEnabled ?? true;
    this.#hasOverridesBeenEdited = false;
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  /**
   * Shows a modal asking the user if they want to discard unsaved changes
   * @returns Promise that resolves to true if user wants to proceed (discard changes), false if user cancels
   */
  async onShowModalUnsavedChanges(): Promise<boolean> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };

    const result: IModalDismissResult = await this.modalUtils.createModal(modalProps);
    return result.role === 'modal-accept';
  }

  //#endregion

}
